<template>
	<Dialog
		:visible="visible"
		:header="title"
		modal
		:closable="!dndInitializing"
		:style="{ width: '26rem' }"
		@update:visible="onVisibleChange"
	>
		<div class="validation-table-excel-reorder-dialog__content">
			<draggable
				v-model="localRows"
				item-key="$id"
				handle=".validation-table-excel-reorder-dialog__handle"
				class="validation-table-excel-reorder-dialog__list"
				:disabled="readonly || dndInitializing"
				@end="onDragEnd"
			>
				<template #item="{ element }">
					<div class="validation-table-excel-reorder-dialog__item">
						<i class="mdi mdi-drag validation-table-excel-reorder-dialog__handle"></i>
						<span class="validation-table-excel-reorder-dialog__pos">{{ element.rowPosition }}</span>
						<span class="validation-table-excel-reorder-dialog__text">{{ element.preview }}</span>
					</div>
				</template>
			</draggable>

			<div v-if="dndInitializing" class="validation-table-excel-reorder-dialog__overlay">
				<ProgressSpinner
					strokeWidth="4"
					animationDuration=".8s"
					class="validation-table-excel-reorder-dialog__spinner"
				/>
				<div class="validation-table-excel-reorder-dialog__overlay-title">
					{{ t('Squeeze.Validation.General.TableV2InitializingTitle') }}
				</div>
				<div class="validation-table-excel-reorder-dialog__overlay-text">
					{{ t('Squeeze.Validation.General.TableValidationInitializing') }}
				</div>
			</div>
		</div>
	</Dialog>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";
import draggable from "vuedraggable";

export type ValidationTableExcelReorderItem = {
	$id: string;
	rowPosition: string;
	preview: string;
};

export default defineComponent({
	name: "ValidationTableExcelReorderDialog",
	components: {
		Dialog,
		ProgressSpinner,
		draggable,
	},
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			required: true,
		},
		rows: {
			type: Array as PropType<ValidationTableExcelReorderItem[]>,
			default: () => [],
		},
		readonly: {
			type: Boolean,
			default: false,
		},
	},
	emits: [
		"update:visible",
		"reorder",
	],
	setup(props, { emit }) {
		const { t } = useI18n();
		const localRows = ref<ValidationTableExcelReorderItem[]>([]);
		const dndInitializing = ref(false);
		const firstDragPending = ref(true);

		const syncRows = () => {
			localRows.value = props.rows.map((row) => ({ ...row }));
		};

		const allowUiToRender = async () => {
			await new Promise<void>((resolve) => {
				window.setTimeout(() => {
					requestAnimationFrame(() => resolve());
				}, 0);
			});
		};

		const onVisibleChange = (nextVisible: boolean) => {
			if (!nextVisible) {
				dndInitializing.value = false;
			}
			emit("update:visible", nextVisible);
		};

		const onDragEnd = async () => {
			if (firstDragPending.value) {
				dndInitializing.value = true;
				await nextTick();
				await allowUiToRender();
				firstDragPending.value = false;
				dndInitializing.value = false;
				await nextTick();
				await allowUiToRender();
			}

			emit("reorder", localRows.value.map((row) => row.$id));
		};

		watch(() => props.visible, (isVisible) => {
			if (isVisible) {
				syncRows();
			} else {
				dndInitializing.value = false;
			}
		}, { immediate: true });

		watch(() => props.rows, () => {
			if (props.visible) {
				syncRows();
			}
		});

		return {
			t,
			dndInitializing,
			localRows,
			onVisibleChange,
			onDragEnd,
		};
	},
});
</script>

<style scoped>
.validation-table-excel-reorder-dialog__content {
	position: relative;
}

.validation-table-excel-reorder-dialog__list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	max-height: 60vh;
	overflow: auto;
	padding: 0.125rem 0;
}

.validation-table-excel-reorder-dialog__item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.625rem 0.75rem;
	border: 1px solid #dfe5e8;
	border-radius: 0.375rem;
	background: #fff;
}

.validation-table-excel-reorder-dialog__handle {
	cursor: grab;
	color: #5b6b73;
}

.validation-table-excel-reorder-dialog__list:deep(.sortable-disabled) .validation-table-excel-reorder-dialog__handle {
	cursor: not-allowed;
	opacity: 0.45;
}

.validation-table-excel-reorder-dialog__pos {
	min-width: 2rem;
	font-weight: 600;
	color: #253238;
}

.validation-table-excel-reorder-dialog__text {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: #253238;
}

.validation-table-excel-reorder-dialog__overlay {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.92);
	z-index: 2;
	text-align: center;
}

.validation-table-excel-reorder-dialog__spinner {
	width: 3rem;
	height: 3rem;
}

.validation-table-excel-reorder-dialog__overlay-title {
	font-weight: 600;
	color: #253238;
}

.validation-table-excel-reorder-dialog__overlay-text {
	max-width: 22rem;
	color: #42535a;
}
</style>
